import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { X, Users } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Floating bottom-right widget that rotates live activity events.
 * Always visible on landing. Can be dismissed; reappears on next session.
 */
const LiveActivityFeed = () => {
  const { t, language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [online, setOnline] = useState(1247);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(() => {
    try {
      return sessionStorage.getItem("live_feed_dismissed") !== "1";
    } catch {
      return true;
    }
  });
  const [expanded, setExpanded] = useState(false);

  // Fetch feed initially + every 45s
  useEffect(() => {
    if (!visible) return;
    const load = () =>
      axios
        .get(`${API}/activity/live-feed?limit=12&lang=${encodeURIComponent(language || "en")}`)
        .then((r) => {
          setEvents(r.data?.events || []);
          setOnline(r.data?.online_users || 0);
        })
        .catch(() => {});
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, [visible, language]);

  // Rotate currently shown event every 8s
  useEffect(() => {
    if (!events.length) return;
    const id = setInterval(() => {
      setCurrentIdx((i) => (i + 1) % events.length);
    }, 7000);
    return () => clearInterval(id);
  }, [events.length]);

  const handleDismiss = () => {
    setVisible(false);
    try { sessionStorage.setItem("live_feed_dismissed", "1"); } catch {}
  };

  const relativeTime = (ts) => {
    if (!ts) return t.laf_just_now || "just now";
    const diff = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return (t.laf_seconds_ago || "{n}s ago").replace("{n}", Math.round(diff));
    if (diff < 3600) return (t.laf_minutes_ago || "{n}m ago").replace("{n}", Math.round(diff / 60));
    return (t.laf_hours_ago || "{n}h ago").replace("{n}", Math.round(diff / 3600));
  };

  if (!visible || !events.length) return null;

  const current = events[currentIdx];
  const displayed = expanded ? events : [current];

  return (
    <div
      data-testid="live-activity-feed"
      className="fixed left-4 bottom-24 sm:bottom-28 z-40 w-[300px] sm:w-[340px]"
    >
      <div
        className="rounded-xl shadow-2xl overflow-hidden border backdrop-blur"
        style={{
          background: "color-mix(in srgb, var(--color-surface) 95%, transparent)",
          borderColor: "var(--color-border)",
        }}
      >
        {/* Header */}
        <div
          className="px-3 py-2 flex items-center justify-between border-b"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-surface-alt)",
          }}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
            <span
              className="text-[10px] font-mono uppercase tracking-widest"
              style={{ color: "var(--color-text-muted)" }}
            >
              {t.laf_live || "LIVE"}
            </span>
            <span className="flex items-center gap-1 text-[11px]" style={{ color: "var(--color-text)" }}>
              <Users className="w-3 h-3" />
              <span className="font-bold">{online.toLocaleString(language || "en")}</span>
              <span style={{ color: "var(--color-text-muted)" }}>{t.laf_online || "online"}</span>
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-[10px] px-1.5 py-0.5 rounded hover:opacity-80"
              style={{ color: "var(--color-text-muted)" }}
              data-testid="live-feed-expand"
            >
              {expanded ? "▲" : "▼"}
            </button>
            <button
              onClick={handleDismiss}
              data-testid="live-feed-close"
              className="text-gray-400 hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Events list */}
        <div className="divide-y" style={{ borderColor: "var(--color-border)" }}>
          {displayed.map((ev, idx) => (
            <div
              key={`${ev.timestamp}-${idx}`}
              className="px-3 py-2.5 flex items-start gap-2 transition-all animate-fade-in"
              data-testid={`activity-event-${idx}`}
            >
              <span className="text-lg leading-none">{ev.flag}</span>
              <div className="flex-1 min-w-0">
                <p
                  className="text-xs leading-snug"
                  style={{ color: "var(--color-text)" }}
                >
                  <span className="font-bold">{ev.name}</span>{" "}
                  {ev.city && (
                    <span style={{ color: "var(--color-text-muted)" }}>
                      {t.laf_from || "from"} {ev.city}
                    </span>
                  )}{" "}
                  {ev.message}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-xs">{ev.icon}</span>
                  <span
                    className="text-[10px] font-mono"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    {relativeTime(ev.timestamp)}
                  </span>
                  {ev.verified && (
                    <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-400 uppercase tracking-wider">
                      ✓ {t.laf_verified || "Verified"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LiveActivityFeed;
